import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import { FaStar, FaQuoteLeft } from "react-icons/fa";
import { motion } from "framer-motion";
import app from "../firebase";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  where,
} from "firebase/firestore";
import ReviewModal from "./ReviewModal";

const db = getFirestore(app);

/* ---------- UI helpers ---------- */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function Stars({ rating = 0 }) {
  const r = clamp(Number(rating) || 0, 0, 5);
  const size = "text-[18px] sm:text-[16px]"; // mobile a bit larger for tapability
  return (
    <div className="flex gap-1" aria-label={`${r} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <FaStar
          key={i}
          className={`${size} ${i < r ? "text-yellow-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );
}

function Avatar({ name = "" }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("");
  return (
    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0d2f5b] text-white grid place-items-center text-[13px] font-semibold">
      {initials || "?"}
    </div>
  );
}

/* ---------- Component ---------- */
export default function TestimonialsCarousel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "testimonials_submissions"),
      where("status", "==", "approved"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) =>
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  // slides & infinite adapt to available items
  const slidesDesktop = useMemo(() => Math.min(3, items.length || 1), [items.length]);
  const slidesTablet = useMemo(() => Math.min(2, items.length || 1), [items.length]);

  const settings = {
    dots: true,
    arrows: false,
    speed: 450,
    autoplay: true,
    autoplaySpeed: 6500,
    pauseOnHover: true,
    adaptiveHeight: false,
    slidesToShow: slidesDesktop,
    slidesToScroll: 1,
    infinite: items.length > slidesDesktop,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: slidesTablet,
          infinite: items.length > slidesTablet,
        },
      },
      {
        breakpoint: 640,
        settings: { slidesToShow: 1, infinite: items.length > 1 },
      },
    ],
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      {/* slick cosmetics + keep dots below the slider */}
      <style>{`
        .t-slider .slick-list { overflow:hidden !important; }
        .t-slider .slick-slide { outline:none; }
        .t-slider .slick-dots { position: static; margin-top: 12px; }
        .t-slider .slick-dots li { margin: 0 5px; }
        .t-slider .slick-dots li button:before { font-size:10px; color:#b8c2d3; }
        .t-slider .slick-dots li.slick-active button:before { color:#ef4a5b; }
        .t-card p { -webkit-line-clamp:4; -webkit-box-orient:vertical; display:-webkit-box; overflow:hidden; }
      `}</style>

      {/* Header (mobile stacks, desktop left + button right) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-[24px] sm:text-[32px] leading-tight font-extrabold text-[#0f2437]">
          What Our Clients Say
        </h2>
        <button
          onClick={() => setOpen(true)}
          className="hidden sm:inline-flex px-4 py-2 rounded-lg bg-[#0d2f5b] text-white text-[14px] font-medium hover:bg-[#123a73] transition"
        >
          Leave a Review
        </button>
      </div>

      {/* Slider */}
      <div className="t-slider">
        {items.length === 0 ? (
          <div className="border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
            <p className="text-[15px] text-gray-600">No testimonials yet.</p>
            <button
              onClick={() => setOpen(true)}
              className="mt-4 inline-flex px-4 py-2 rounded-lg bg-[#0d2f5b] text-white text-[14px] font-medium hover:bg-[#123a73] transition"
            >
              Be the first to review
            </button>
          </div>
        ) : (
          <Slider {...settings}>
            {items.map((t) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="px-1.5 sm:px-2"
              >
                {/* Card — no shadow; mobile padding tuned; no weird top border */}
                <div className="t-card h-full rounded-2xl bg-white border border-gray-200 overflow-hidden">
                  <div className="p-4 sm:p-5 md:p-6 flex flex-col h-full">
                    <FaQuoteLeft className="text-[20px] sm:text-[22px] text-[#0d2f5b]/30 mb-2 sm:mb-3" />
                    <p className="text-[#0f2437]/90 text-[15px] sm:text-[15px] leading-7 italic mb-3 sm:mb-4">
                      {t.message ? `“${t.message}”` : "“”"}
                    </p>
                    <Stars rating={t.rating} />
                    <div className="mt-4 sm:mt-5 flex items-center gap-3">
                      <Avatar name={t.name} />
                      <div>
                        <div className="text-[#0f2437] font-semibold text-[15px] leading-5">
                          {t.name}
                        </div>
                        <div className="text-[12px] text-gray-500 tracking-wide uppercase">
                          {t.location || ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </Slider>
        )}
      </div>

      {/* Mobile CTA (full-width) */}
      <div className="sm:hidden mt-4">
        <button
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center px-5 py-3 rounded-lg bg-[#0d2f5b] text-white text-[15px] font-medium hover:bg-[#123a73] transition"
        >
          Leave a Review
        </button>
      </div>

      {/* Use your existing ReviewModal; support multiple prop names so it “just works” */}
      <ReviewModal
        open={open}
        isOpen={open}
        show={open}
        visible={open}
        onClose={() => setOpen(false)}
        onRequestClose={() => setOpen(false)}
        onHide={() => setOpen(false)}
        closeModal={() => setOpen(false)}
      />
    </div>
  );
}
